import { useState, useEffect, useCallback } from 'react';
import type { Lead, Opportunity, Activity } from '../../types';
import type { PipelineStats, StageStat } from '../../api';
import type { WonLostTrendPoint, ActivityCompletionData } from './components';
import {
  fetchPipelineStats,
  fetchOpportunities,
  fetchLeads,
  fetchActivities,
} from '../../api';

interface DashboardMetrics {
  totalPipelineValue: number;
  dealsToCloseThisMonth: number;
  conversionRate: number;
  avgDealSize: number;
  dealsWonThisMonth: {
    count: number;
    value: number;
  };
  activitiesDue: number;
}

interface UseDashboardDataReturn {
  // Raw data
  pipelineStats: PipelineStats | null;
  stages: StageStat[];
  
  // Computed metrics
  metrics: DashboardMetrics;
  wonLostTrend: WonLostTrendPoint[];
  activityCompletion: ActivityCompletionData[];
  upcomingActivities: Activity[];
  recentDealsWon: Opportunity[];
  hotLeads: Lead[];
  
  // Status
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useDashboardData(): UseDashboardDataReturn {
  const [pipelineStats, setPipelineStats] = useState<PipelineStats | null>(null);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [statsRes, oppsRes, leadsRes, activitiesRes] = await Promise.all([
        fetchPipelineStats().catch(() => null),
        fetchOpportunities().catch(() => []),
        fetchLeads({ limit: 100 }).catch(() => ({ data: [] })),
        fetchActivities({ limit: 100 }).catch(() => ({ data: [] })),
      ]);
      
      setPipelineStats(statsRes);
      setOpportunities(Array.isArray(oppsRes) ? oppsRes : []);
      setLeads(leadsRes.data || []);
      setActivities(activitiesRes.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Compute dashboard metrics
  const metrics = computeMetrics(opportunities, leads, activities, pipelineStats);
  const wonLostTrend = computeWonLostTrend(opportunities);
  const activityCompletion = computeActivityCompletion(activities);
  const upcomingActivities = getUpcomingActivities(activities, 5);
  const recentDealsWon = getRecentDealsWon(opportunities, 5);
  const hotLeads = getHotLeads(leads, 5);

  return {
    pipelineStats,
    stages: pipelineStats?.stages || [],
    metrics,
    wonLostTrend,
    activityCompletion,
    upcomingActivities,
    recentDealsWon,
    hotLeads,
    loading,
    error,
    refetch: fetchData,
  };
}

function computeMetrics(
  opportunities: Opportunity[],
  leads: Lead[],
  activities: Activity[],
  pipelineStats: PipelineStats | null
): DashboardMetrics {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  // Deals to close this month (open opps with expected close date this month)
  const dealsToCloseThisMonth = opportunities.filter((opp) => {
    if (opp.closed_reason) return false;
    if (!opp.expected_close_date) return false;
    const closeDate = new Date(opp.expected_close_date);
    return closeDate >= startOfMonth && closeDate <= endOfMonth;
  }).length;

  // Conversion rate: leads converted / total leads
  const totalLeads = leads.length;
  const convertedLeads = leads.filter((l) => l.status === 'converted').length;
  const conversionRate = totalLeads > 0 ? (convertedLeads / totalLeads) * 100 : 0;

  // Deals won this month
  const dealsWonThisMonth = opportunities.filter((opp) => {
    if (opp.closed_reason !== 'won') return false;
    if (!opp.actual_close_date) return false;
    const closeDate = new Date(opp.actual_close_date);
    return closeDate >= startOfMonth && closeDate <= endOfMonth;
  });

  // Activities due (scheduled and not completed)
  const activitiesDue = activities.filter((act) => {
    if (act.status !== 'scheduled') return false;
    if (!act.due_date) return false;
    return new Date(act.due_date) <= endOfMonth;
  }).length;

  return {
    totalPipelineValue: pipelineStats?.total_pipeline_value || 0,
    dealsToCloseThisMonth,
    conversionRate,
    avgDealSize: pipelineStats?.avg_deal_size || 0,
    dealsWonThisMonth: {
      count: dealsWonThisMonth.length,
      value: dealsWonThisMonth.reduce((sum, opp) => sum + opp.amount, 0),
    },
    activitiesDue,
  };
}

function computeWonLostTrend(opportunities: Opportunity[]): WonLostTrendPoint[] {
  const now = new Date();
  const months: WonLostTrendPoint[] = [];

  for (let i = 5; i >= 0; i--) {
    const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
    const monthLabel = monthDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });

    const monthOpps = opportunities.filter((opp) => {
      if (!opp.actual_close_date) return false;
      const closeDate = new Date(opp.actual_close_date);
      return closeDate >= monthDate && closeDate <= monthEnd;
    });

    const won = monthOpps.filter((o) => o.closed_reason === 'won');
    const lost = monthOpps.filter((o) => o.closed_reason === 'lost');

    months.push({
      date: monthLabel,
      won: won.length,
      lost: lost.length,
      wonValue: won.reduce((sum, o) => sum + o.amount, 0),
      lostValue: lost.reduce((sum, o) => sum + o.amount, 0),
    });
  }

  return months;
}

function computeActivityCompletion(activities: Activity[]): ActivityCompletionData[] {
  const now = new Date();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - now.getDay());
  startOfWeek.setHours(0, 0, 0, 0);

  const days: ActivityCompletionData[] = [];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  for (let i = 0; i < 7; i++) {
    const dayStart = new Date(startOfWeek);
    dayStart.setDate(startOfWeek.getDate() + i);
    const dayEnd = new Date(dayStart);
    dayEnd.setHours(23, 59, 59, 999);

    const dayActivities = activities.filter((act) => {
      const dueDate = act.due_date ? new Date(act.due_date) : null;
      const completedDate = act.completed_at ? new Date(act.completed_at) : null;
      
      if (dueDate && dueDate >= dayStart && dueDate <= dayEnd) return true;
      if (completedDate && completedDate >= dayStart && completedDate <= dayEnd) return true;
      return false;
    });

    days.push({
      day: dayNames[i],
      completed: dayActivities.filter((a) => a.status === 'completed').length,
      scheduled: dayActivities.filter((a) => a.status === 'scheduled').length,
    });
  }

  return days;
}

function getUpcomingActivities(activities: Activity[], limit: number): Activity[] {
  const now = new Date();
  
  return activities
    .filter((act) => {
      if (act.status !== 'scheduled') return false;
      if (!act.due_date) return false;
      return new Date(act.due_date) >= now;
    })
    .sort((a, b) => {
      const dateA = new Date(a.due_date!);
      const dateB = new Date(b.due_date!);
      return dateA.getTime() - dateB.getTime();
    })
    .slice(0, limit);
}

function getRecentDealsWon(opportunities: Opportunity[], limit: number): Opportunity[] {
  return opportunities
    .filter((opp) => opp.closed_reason === 'won' && opp.actual_close_date)
    .sort((a, b) => {
      const dateA = new Date(a.actual_close_date!);
      const dateB = new Date(b.actual_close_date!);
      return dateB.getTime() - dateA.getTime();
    })
    .slice(0, limit);
}

function getHotLeads(leads: Lead[], limit: number): Lead[] {
  return leads
    .filter((lead) => lead.status !== 'converted' && lead.status !== 'unqualified')
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}
